import cn from 'classnames';
import { FC, ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import {
  ReportPrintSettings,
  ReportPrintSettingsPrintType,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import printTypeBW from '@/shared/assets/images/printTypeButtonBW.png';
import printTypeColored from '@/shared/assets/images/printTypeButtonColored.png';
import { PrintType as PrintColorType } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Button, Checkbox, RadioGroup, WidgetCard } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';

import { useDownloadReportPDF } from '@/features/downloadReportPDF';
import {
  useFDA3DGPRestrictions,
  useFDAEnvironment,
} from '@/features/fdaRestrictions';
import { maskFiltersModel } from '@/features/renderMasks';

import styles from './SettingsBar.module.scss';
type SettingsBarProps = {
  className?: string;
  settings: ReportPrintSettings;
  reportID: string;
  onChange: (newPrintSettings: ReportPrintSettings) => void;
  isLoading?: boolean;
  patientID?: string;
};

export const SettingsBar: FC<SettingsBarProps> = (props) => {
  const { className, settings, reportID, onChange, isLoading } = props;

  const { formatMessage } = useIntl();

  const report = useAppSelector(reportsModel.selectors.selectByID(reportID));
  const isReportSigned = report?.Signature?.Signed;
  const reportType = report?.Type;

  const hideProbability = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_Probabilities,
    ),
  );
  const hideNonDentalFindings = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_NonDentalFindings,
    ),
  );

  const showExtendedConclusion = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_CBCTExtendedConclusion,
    ),
  );

  const { isConditionsMasksHidden, isAnatomyMasksHidden, isCBCTMasksHidden } =
    useAppSelector(maskFiltersModel.selectors.selectMasksFlags);

  const {
    shouldHideProbabilities,
    shouldHideOtherFindings,
    shouldRestrictPrint,
  } = useFDA3DGPRestrictions();

  const { isFDA } = useFDAEnvironment();

  // FDA: mask visibility on the preview is fully derived from the FDA mode
  // (aided shows the allowed masks, unaided shows none), so the manual
  // "Show masks" toggle is hidden for both
  const hideMasksAndBBoxes =
    (isConditionsMasksHidden && isAnatomyMasksHidden) ||
    (reportType === ReportType.ReportType_CBCT_GP && isCBCTMasksHidden) ||
    isFDA;

  const { downloadReportPDF, downloading } = useDownloadReportPDF(reportID);

  const {
    IsInverted,
    BlackAndWhiteMode,
    ShowBottomJaw,
    ShowTopJaw,
    ShowSlices,
    ShowMasks,
    ShowDentalChart,
    ShowDiagnosesProbabilities,
    ShowDiagnoses,
    PrintType,
    ShowBBoxes,
    ShowStudyMetadata,
  } = settings;

  const showDentalChartHandle = () => {
    // Checking if all elements (ShowDentalChart, ShowTopJaw, ShowBottomJaw) are disabled
    const isAllDisabled = !ShowDentalChart && !ShowTopJaw && !ShowBottomJaw;

    trackEvent({
      eventName: 'PreviewReportPageToothChartCheckboxClicked',
      properties: {
        tog_status: ShowDentalChart ? 'disabled' : 'enabled',
      },
    });

    onChange({
      ...settings,
      ShowDentalChart: !ShowDentalChart,
      // If all elements are disabled, then set ShowTopJaw and ShowBottomJaw to the opposite value ShowDentalChart,
      // otherwise use the current value of ShowTopJaw
      ShowTopJaw: isAllDisabled ? !ShowDentalChart : ShowTopJaw,
      ShowBottomJaw: isAllDisabled ? !ShowDentalChart : ShowBottomJaw,
    } as ReportPrintSettings);
  };

  const showUpperJawHandle = () => {
    trackEvent({
      eventName: 'PreviewReportPageUpperJawCheckboxClicked',
      properties: {
        tog_status: ShowTopJaw ? 'disabled' : 'enabled',
      },
    });
    onChange({
      ...settings,
      ShowTopJaw: !ShowTopJaw,
    } as ReportPrintSettings);
  };

  const showLowerJawHandle = () => {
    trackEvent({
      eventName: 'PreviewReportPageLowerJawCheckboxClicked',
      properties: {
        tog_status: ShowBottomJaw ? 'disabled' : 'enabled',
      },
    });
    onChange({
      ...settings,
      ShowBottomJaw: !ShowBottomJaw,
    } as ReportPrintSettings);
  };

  useEffect(() => {
    if (
      settings.ShowDentalChart &&
      !settings.ShowBottomJaw &&
      !settings.ShowTopJaw
    ) {
      onChange({
        ...settings,
        ShowDentalChart: false,
      } as ReportPrintSettings);
    }
  }, [settings, onChange]);

  return (
    <WidgetCard
      className={cn(styles.container, className)}
      data-component="preview-report-settings"
    >
      <div className={styles.content}>
        <h4 className="h4">
          <FormattedMessage
            id="reportPreview.settings.label"
            defaultMessage="Settings"
          />
        </h4>

        <h3>
          <FormattedMessage
            id="reportPreview.settings.printType"
            defaultMessage="Print type"
          />
        </h3>

        <RadioGroup
          name="print-type"
          innerClassName={styles.printTypeSwitch}
          value={
            BlackAndWhiteMode
              ? PrintColorType.blackAndWhite
              : PrintColorType.colored
          }
          onChange={() => {
            trackEvent({
              eventName: BlackAndWhiteMode
                ? 'PreviewReportPageColorSettingButtonClicked'
                : 'PreviewReportPageB&WSettingButtonClicked',
            });
            onChange({
              ...settings,
              BlackAndWhiteMode: !BlackAndWhiteMode,
            } as ReportPrintSettings);
          }}
          items={
            [
              {
                value: PrintColorType.blackAndWhite,
                label: (
                  <img
                    className={styles.printTypeButton}
                    src={printTypeBW}
                    alt={formatMessage({
                      id: 'printTypeButton.blackAndWhite',
                      defaultMessage: 'Black And White',
                    })}
                  />
                ),
                disabled: isLoading,
              },
              {
                value: PrintColorType.colored,
                label: (
                  <img
                    className={styles.printTypeButton}
                    src={printTypeColored}
                    alt={formatMessage({
                      id: 'printTypeButton.colored',
                      defaultMessage: 'Colored',
                    })}
                  />
                ),
                disabled: isLoading,
              },
            ] as {
              value: PrintColorType;
              label: ReactNode;
              disabled: boolean;
            }[]
          }
        />

        <div className={styles.checkboxContainer}>
          <Checkbox
            disabled={isLoading}
            checked={IsInverted}
            onChange={() => {
              trackEvent({
                eventName: 'PreviewReportPageInvertedCheckboxClicked',
                properties: {
                  tog_status: IsInverted ? 'disabled' : 'enabled',
                },
              });
              onChange({
                ...settings,
                IsInverted: !IsInverted,
              } as ReportPrintSettings);
            }}
          >
            <FormattedMessage
              id="reportPreview.settings.inverted"
              defaultMessage="Inverted"
            />
          </Checkbox>

          {PrintType ===
            ReportPrintSettingsPrintType.ReportPrintSettings_PrintType_Full &&
            !hideProbability &&
            !shouldHideProbabilities && (
              <Checkbox
                disabled={isLoading}
                checked={ShowDiagnosesProbabilities}
                onChange={() => {
                  trackEvent({
                    eventName: 'PreviewReportPageProbabilityCheckboxClicked',
                    properties: {
                      tog_status: ShowDiagnosesProbabilities
                        ? 'disabled'
                        : 'enabled',
                    },
                  });
                  onChange({
                    ...settings,
                    ShowDiagnosesProbabilities: !ShowDiagnosesProbabilities,
                  } as ReportPrintSettings);
                }}
              >
                <FormattedMessage
                  id="reportPreview.settings.probability"
                  defaultMessage="Probability"
                />
              </Checkbox>
            )}

          <Checkbox
            disabled={isLoading}
            checked={ShowDentalChart}
            onChange={showDentalChartHandle}
          >
            <FormattedMessage
              id="reportPreview.settings.dentalChart"
              defaultMessage="Tooth chart"
            />
          </Checkbox>

          <Checkbox
            disabled={isLoading}
            className={styles.nestedCheckbox}
            checked={ShowTopJaw}
            onChange={showUpperJawHandle}
          >
            <FormattedMessage
              id="reportPreview.settings.upperJaw"
              defaultMessage="Upper jaw"
            />
          </Checkbox>

          <Checkbox
            disabled={isLoading}
            className={styles.nestedCheckbox}
            checked={ShowBottomJaw}
            onChange={showLowerJawHandle}
          >
            <FormattedMessage
              id="reportPreview.settings.lowerJaw"
              defaultMessage="Lower jaw"
            />
          </Checkbox>

          <Checkbox
            disabled={isLoading}
            checked={ShowDiagnoses}
            onChange={() => {
              trackEvent({
                eventName: 'PreviewReportPageConditionsCheckboxClicked',
                properties: {
                  tog_status: ShowDiagnoses ? 'disabled' : 'enabled',
                },
              });
              onChange({
                ...settings,
                ShowDiagnoses: !ShowDiagnoses,
              } as ReportPrintSettings);
            }}
          >
            <FormattedMessage
              id="reportPreview.settings.conditions"
              defaultMessage="Conditions"
            />
          </Checkbox>

          {PrintType ===
            ReportPrintSettingsPrintType.ReportPrintSettings_PrintType_Full && (
            <Checkbox
              disabled={isLoading}
              checked={ShowSlices}
              onChange={() => {
                trackEvent({
                  eventName: 'PreviewReportPageSlicesCheckboxClicked',
                  properties: {
                    tog_status: ShowSlices ? 'disabled' : 'enabled',
                  },
                });
                onChange({
                  ...settings,
                  ShowSlices: !ShowSlices,
                } as ReportPrintSettings);
              }}
            >
              <FormattedMessage
                id="reportPreview.settings.slices"
                defaultMessage="Slices"
              />
            </Checkbox>
          )}

          {!hideMasksAndBBoxes && (
            <Checkbox
              disabled={isLoading}
              checked={ShowMasks}
              onChange={() => {
                trackEvent({
                  eventName: 'PreviewReportPageShowMasksCheckboxClicked',
                  properties: {
                    tog_status: ShowMasks ? 'disabled' : 'enabled',
                  },
                });
                onChange({
                  ...settings,
                  ShowMasks: !ShowMasks,
                } as ReportPrintSettings);
              }}
            >
              <FormattedMessage
                id="reportPreview.settings.masks"
                defaultMessage="Show masks"
              />
            </Checkbox>
          )}

          {!hideNonDentalFindings &&
            !shouldHideOtherFindings &&
            reportType !== ReportType.ReportType_IOXRay_GP && (
              <Checkbox
                disabled={isLoading}
                checked={ShowBBoxes}
                onChange={() => {
                  trackEvent({
                    eventName: 'PreviewReportPageOtherFindingsCheckboxClicked',
                    properties: {
                      tog_status: ShowBBoxes ? 'disabled' : 'enabled',
                    },
                  });
                  onChange({
                    ...settings,
                    ShowBBoxes: !ShowBBoxes,
                  } as ReportPrintSettings);
                }}
              >
                <FormattedMessage
                  id="reportPreview.settings.nonDentalFindings"
                  defaultMessage="Other findings"
                />
              </Checkbox>
            )}

          <Checkbox
            disabled={isLoading}
            checked={ShowStudyMetadata}
            onChange={() => {
              trackEvent({
                eventName: 'PreviewReportPageStudyInfoCheckboxClicked',
                properties: {
                  tog_status: ShowStudyMetadata ? 'disabled' : 'enabled',
                },
              });
              onChange({
                ...settings,
                ShowStudyMetadata: !ShowStudyMetadata,
              } as ReportPrintSettings);
            }}
          >
            <FormattedMessage
              id="reportPreview.settings.studyInfo"
              defaultMessage="Study meta info"
            />
          </Checkbox>
        </div>

        <div className={styles.status}>
          <p className={isReportSigned ? styles.signed : styles.notSigned}>
            {isReportSigned ? (
              <FormattedMessage
                id="reportPreview.settings.signed"
                defaultMessage="Signed by doctor"
              />
            ) : (
              <FormattedMessage
                id="reportPreview.settings.notSigned"
                defaultMessage="Not signed by doctor"
              />
            )}
          </p>

          {!isReportSigned && !showExtendedConclusion && (
            <p>
              <FormattedMessage
                id="reportPreview.settings.disclaimer"
                defaultMessage="If you want the Conclusion to be displayed in printed version or PDF, it is necessary to sign the report at the previous step"
              />
            </p>
          )}
        </div>

        <div className={styles.printAndDownload}>
          <Button
            onClick={() => {
              trackEvent({
                eventName: 'PreviewReportPagePrintButtonClicked',
              });
              window?.print();
            }}
            block
            disabled={shouldRestrictPrint}
          >
            <FormattedMessage id="global.print" defaultMessage="Print" />
          </Button>

          <Button
            size="medium"
            variant="tertiary"
            onClick={() => {
              trackEvent({
                eventName: 'PreviewReportPageDownloadPDFButtonClicked',
              });
              downloadReportPDF();
            }}
            loading={downloading}
            disabled={shouldRestrictPrint}
          >
            <FormattedMessage
              id="global.reportPreview.settings.downloadPDF"
              defaultMessage="Download PDF"
            />
          </Button>
        </div>
      </div>
    </WidgetCard>
  );
};
